import { useState, useEffect, useMemo, Fragment } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ResponsiveContainer } from "@/components/ui/responsive-container";
import type { IntakeData } from "@/lib/types";
import { Trans, msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { scrollToFirstError } from "@/lib/utils";
import { DENTISTRY_PROCEDURE_TYPE_ORDER } from "@/intake-v2/buildCardFlow";

function normalizeDentalProcedureTypes(
  v: string | string[] | undefined,
): string[] {
  if (!v) return [];
  const arr = Array.isArray(v) ? v : [v];
  return arr.map((s) => String(s).trim()).filter(Boolean);
}

interface DynamicQuestionTreeProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onNext: () => void;
  onBack: () => void;
}

export const QUESTION_TREE = {
  orthodontics: {
    title: <Trans>Orthodontic Treatment Questions</Trans>,
    questions: [
      {
        id: "orthodonticHistory",
        type: "select",
        label: <Trans>Have you had orthodontic treatment before?</Trans>,
        options: [
          msg`No`,
          msg`Yes - braces`,
          msg`Yes - Invisalign`,
          msg`Yes - retainers`,
          msg`Yes - other`,
        ],
      },
      {
        id: "orthodonticGoals",
        type: "multiselect",
        label: <Trans>What are your main orthodontic goals?</Trans>,
        options: [
          msg`Straighten crooked teeth`,
          msg`Close gaps between teeth`,
          msg`Fix overbite/underbite`,
          msg`Improve bite alignment`,
          msg`Enhance smile aesthetics`,
          msg`Prepare for other dental work`,
        ],
      },
      {
        id: "treatmentDuration",
        type: "select",
        label: <Trans>What is your preferred treatment timeline?</Trans>,
        options: [
          msg`6-12 months`,
          msg`1-2 years`,
          msg`2+ years`,
          msg`Whatever is necessary`,
          msg`As fast as possible`,
        ],
      },
      {
        id: "appliancePreference",
        type: "select",
        label:  <Trans>Do you have a preference for type of braces?</Trans>,
        options: [
          msg`Traditional metal braces`,
          msg`Ceramic braces`,
          msg`Lingual braces`,
          msg`Clear aligners`,
          msg`No preference`,
        ],
      },
    ],
  },
  implants: {
    title: <Trans>Dental Implant Questions</Trans>,
    questions: [
      {
        id: "missingTeethCount",
        type: "select",
        label: <Trans>How many teeth are you missing?</Trans>,
        options: [
          msg`1 tooth`,
          msg`2-3 teeth`,
          msg`4-6 teeth`,
          msg`7+ teeth`,
          msg`Full arch (upper)`,
          msg`Full arch (lower)`,
          msg`Both arches`,
        ],
      },
      {
        id: "missingTeethDuration",
        type: "select",
        label: <Trans>How long have the teeth been missing?</Trans>,
        options: [
          msg`Less than 6 months`,
          msg`6 months - 1 year`,
          msg`1-2 years`,
          msg`2-5 years`,
          msg`5+ years`,
        ],
      },
      {
        id: "boneGraftHistory",
        type: "select",
        label: <Trans>Have you had bone grafting procedures?</Trans>,
        options: [
          msg`No`,
          msg`Yes - recent (within 6 months)`,
          msg`Yes - past`,
          msg`Unsure`,
          msg`Recommended but not done`,
        ],
      },
      {
        id: "implantExpectations",
        type: "multiselect",
        label: <Trans>What are your expectations for dental implants?</Trans>,
        options: [
          msg`Restore chewing function`,
          msg`Improve speech`,
          msg`Enhance appearance`,
          msg`Prevent bone loss`,
          msg`Avoid removable dentures`,
          msg`Long-term solution`,
        ],
      },
    ],
  },  
  cosmetic: {
    title: <Trans>Cosmetic Dentistry Questions</Trans>,
    questions: [
      {
        id: "cosmeticGoals",
        type: "multiselect",
        label: <Trans>What cosmetic improvements are you seeking?</Trans>,
        options: [
          msg`Whiter teeth`,
          msg`Straighter teeth`,
          msg`Close gaps`,
          msg`Fix chipped teeth`,
          msg`Improve gum line`,
          msg`Replace old fillings`,
          msg`Full smile makeover`,
        ],
      },
      {
        id: "previousCosmetic",
        type: "select",
        label: <Trans>Have you had cosmetic dental work before?</Trans>,
        options: [
          msg`No`,
          msg`Teeth whitening`,
          msg`Veneers`,
          msg`Bonding`,
          msg`Crowns`,
          msg`Multiple procedures`,
        ],
      },
      {
        id: "smileConfidence",
        type: "select",
        label: <Trans>How confident are you with your current smile?</Trans>,
        options: [
          msg`Very confident`,
          msg`Somewhat confident`,
          msg`Not very confident`,
          msg`Not confident at all`,
        ],
      },
      {
        id: "socialImpact",
        type: "select",
        label: <Trans>Does your smile affect your social or professional life?</Trans>,
        options: [
          msg`Not at all`,
          msg`Slightly`,
          msg`Moderately`,
          msg`Significantly`,
          msg`Very significantly`,
        ],
      },      
    ],
  },
  restoration: {
    title: <Trans>Full Mouth Reconstruction Questions</Trans>,
    questions: [
      {
        id: "reconstructionReason",
        type: "multiselect",
        label: <Trans>What issues require full mouth reconstruction?</Trans>,
        options: [
          msg`Severe tooth decay`,
          msg`Advanced gum disease`,
          msg`Trauma/injury`,
          msg`Severe wear/grinding`,
          msg`Failed previous dental work`,
          msg`Congenital conditions`,
          msg`TMJ disorders`,
        ],
      },
      {
        id: "currentPainLevel",
        type: "select",
        label: <Trans>What is your current pain/discomfort level?</Trans>,
        options: [
          msg`No pain`,
          msg`Mild discomfort`,
          msg`Moderate pain`,
          msg`Severe pain`,
          msg`Constant pain`,
        ],
      },
      {
        id: "functionIssues",
        type: "multiselect",
        label: <Trans>What functional problems are you experiencing?</Trans>,
        options: [
          msg`Difficulty chewing`,
          msg`Speech problems`,
          msg`Jaw pain/clicking`,
          msg`Teeth sensitivity`,
          msg`Frequent infections`,
          msg`Bad breath`,
          msg`Loose teeth`,
        ],
      },
      {
        id: "treatmentPriority",
        type: "select",
        label: <Trans>What is your main priority for treatment?</Trans>,
        options: [
          msg`Pain relief`,
          msg`Function restoration`,
          msg`Aesthetic improvement`,
          msg`Long-term health`,
          msg`All of the above`,
        ],
      },
    ],
  },
  veneers: {
    title: <Trans>Veneer Treatment Questions</Trans>,
    questions: [
      {
        id: "veneerGoals",
        type: "multiselect",
        label: <Trans>What improvements are you seeking with veneers?</Trans>,
        options: [
          msg`Whiter teeth`,
          msg`Straighter appearance`,
          msg`Close gaps between teeth`,
          msg`Repair chipped teeth`,
          msg`Change tooth shape`,
          msg`Create uniform smile`,
          msg`Cover stains or discoloration`,
        ],
      },
      {
        id: "teethToTreat",
        type: "select",
        label: <Trans>How many teeth are you considering for veneers?</Trans>,
        options: [
          msg`2-4 teeth`,
          msg`6-8 teeth`,
          msg`10-12 teeth`,
          msg`Full smile makeover`,
          msg`Not sure yet`,
        ],
      },
      {
        id: "currentTeethCondition",
        type: "select",
        label: <Trans>What is the current condition of your teeth?</Trans>,
        options: [
          msg`Healthy with minor cosmetic issues`,
          msg`Some chips or cracks`,
          msg`Staining or discoloration`,
          msg`Gaps between teeth`,
          msg`Multiple cosmetic concerns`,
        ],
      },
      {
        id: "previousCosmetic",
        type: "select",
        label: <Trans>Have you had cosmetic dental work before?</Trans>,
        options: [
          msg`No`,
          msg`Teeth whitening`,
          msg`Bonding`,
          msg`Veneers`,
          msg`Crowns`,
          msg`Multiple procedures`,
        ],
      },
    ],
  },
  crowns: {
    title: <Trans>Crown Treatment Questions</Trans>,
    questions: [
      {
        id: "crownReason",
        type: "multiselect",
        label: <Trans>Why do you need crowns?</Trans>,
        options: [
          msg`Protect damaged tooth`,
          msg`Restore broken tooth`,
          msg`Cover large filling`,
          msg`After root canal`,
          msg`Cosmetic improvement`,
          msg`Replace old crown`,
          msg`Strengthen weak tooth`,
        ],
      },
      {
        id: "numberOfCrowns",
        type: "select",
        label: <Trans>How many crowns do you need?</Trans>,
        options: [
          msg`1 crown`,
          msg`2-3 crowns`,
          msg`4-6 crowns`,
          msg`7+ crowns`,
          msg`Not sure`,
        ],
      },
      {
        id: "crownMaterial",
        type: "select",
        label: <Trans>Do you have a preference for crown material?</Trans>,
        options: [
          msg`Porcelain (most natural)`,
          msg`Porcelain-fused-to-metal`,
          msg`All-ceramic`,
          msg`Gold`,
          msg`No preference`,
        ],
      },
      {
        id: "urgencyLevel",
        type: "select",
        label: <Trans>How urgent is your need for treatment?</Trans>,
        options: [
          msg`Emergency - pain or broken tooth`,
          msg`Soon - within 1 month`,
          msg`Planning ahead - 2-3 months`,
          msg`Future planning - flexible timing`,
        ],
      },
    ],
  },
  whitening: {
    title: <Trans>Teeth Whitening Questions</Trans>,
    questions: [
      {
        id: "whiteningGoal",
        type: "select",
        label: <Trans>What level of whitening are you seeking?</Trans>,
        options: [
          msg`Light whitening - 2-3 shades`,
          msg`Moderate whitening - 4-6 shades`,
          msg`Dramatic whitening - 7+ shades`,
          msg`Just want to remove stains`,
          msg`Not sure`,
        ],
      },
      {
        id: "stainingType",
        type: "multiselect",
        label: <Trans>What type of staining do you have?</Trans>,
        options: [
          msg`Coffee/tea stains`,
          msg`Wine stains`,
          msg`Tobacco stains`,
          msg`Age-related yellowing`,
          msg`Medication staining`,
          msg`Fluorosis spots`,
          msg`General discoloration`,
        ],
      },
      {
        id: "previousWhitening",
        type: "select",
        label: <Trans>Have you tried teeth whitening before?</Trans>,
        options: [
          msg`No`,
          msg`Over-the-counter products`,
          msg`Professional whitening`,
          msg`Home whitening trays`,
          msg`Multiple attempts`,
        ],
      },
      {
        id: "sensitivityConcern",
        type: "select",
        label: <Trans>Do you have sensitive teeth?</Trans>,
        options: [
          msg`No sensitivity issues`,
          msg`Mild sensitivity`,
          msg`Moderate sensitivity`,
          msg`Severe sensitivity`,
          msg`Not sure`,
        ],
      },
    ],
  },
  other: {
    title: <Trans>General Dental Questions</Trans>,
    questions: [
      {
        id: "lastDentalVisit",
        type: "select",
        label: <Trans>When was your last dental visit?</Trans>,
        options: [
          msg`Within 6 months`,
          msg`6 months - 1 year`,
          msg`1-2 years`,
          msg`2-5 years`,
          msg`5+ years`,
          msg`Never`,
        ],
      },
      {
        id: "dentalConcerns",
        type: "multiselect",
        label: <Trans>What dental concerns do you have?</Trans>,
        options: [
          msg`Tooth pain`,
          msg`Gum problems`,
          msg`Bad breath`,
          msg`Teeth sensitivity`,
          msg`Cosmetic concerns`,
          msg`Bite problems`,
          msg`Preventive care`,
          msg`No specific concerns`,
        ],
      },
      {
        id: "treatmentInterest",
        type: "multiselect",
        label: <Trans>What treatments are you interested in learning about?</Trans>,
        options: [
          msg`Preventive care`,
          msg`Fillings/restorations`,
          msg`Teeth whitening`,
          msg`Orthodontics`,
          msg`Implants`,
          msg`Cosmetic dentistry`,
          msg`Gum treatment`,
          msg`Not sure yet`,
        ],
      },
    ],
  },  
};

export default function DynamicQuestionTree({
  data,
  onUpdate,
  onNext,
  onBack,
}: DynamicQuestionTreeProps) {
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const { i18n } = useLingui();

  const procedureSections = useMemo(() => {
    const selected = normalizeDentalProcedureTypes(data.dentalProcedureType);
    if (selected.length === 0) return [];
    const orderSet = new Set<string>(
      DENTISTRY_PROCEDURE_TYPE_ORDER as unknown as string[],
    );
    const ordered = DENTISTRY_PROCEDURE_TYPE_ORDER.filter((t) =>
      selected.includes(t),
    );
    const extras = selected.filter((t) => !orderSet.has(t));
    const typesOrdered = [...ordered, ...extras];
    return typesOrdered
      .map((typeKey) => {
        const tree = QUESTION_TREE[typeKey as keyof typeof QUESTION_TREE];
        return tree ? { typeKey, tree } : null;
      })
      .filter(
        (s): s is {
          typeKey: string;
          tree: (typeof QUESTION_TREE)[keyof typeof QUESTION_TREE];
        } => s !== null,
      );
  }, [data.dentalProcedureType]);

  useEffect(() => {
    if (procedureSections.length === 0) return;
    const initialData: Record<string, unknown> = {};
    procedureSections.forEach(({ tree }) => {
      tree.questions.forEach((q) => {
        initialData[q.id] =
          (data as any)[q.id] ?? (q.type === "multiselect" ? [] : "");
      });
    });
    setFormData(initialData);
  }, [data.dentalProcedureType, procedureSections]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev: any) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleMultiSelectChange = (
    field: string,
    option: string,
    checked: boolean,
  ) => {
    setFormData((prev: any) => {
      const currentValues = prev[field] || [];
      if (checked) { 
        return { ...prev, [field]: [...currentValues, option] };
      } else {
        return {
          ...prev,
          [field]: currentValues.filter((item: string) => item !== option),
        };
      }
    });
  };

  const validateForm = () => { 
    const newErrors: any = {};
  
    procedureSections.forEach(({ tree }) => {
      tree.questions.forEach((question) => {
        const value = formData[question.id];
  
        if (question.type === "multiselect") {
          if (!Array.isArray(value) || value.length === 0) {
            newErrors[question.id] = i18n._(
              msg`Please select at least one option`,
            );
          }
        } else {
          const safeValue = typeof value === "string" ? value.trim() : value;
  
          if (!safeValue) {
            newErrors[question.id] = i18n._(msg`This field is required`);
          }
        }
      });
    });
  
    setErrors(newErrors);
  
    if (Object.keys(newErrors).length > 0) {
      scrollToFirstError();
    }
  
    return Object.keys(newErrors).length === 0;
  };  

  const handleSubmit = () => {
    if (validateForm()) {
      onUpdate(formData);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => {
        onNext(); 
      }, 100);
    }
  };

  const selectedTypes = normalizeDentalProcedureTypes(data.dentalProcedureType);

  // Only show fallback if dental procedure type(s) set but no matching question trees
  if (selectedTypes.length > 0 && procedureSections.length === 0) {
    // If no specific questions for this procedure type, just continue
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <span className="text-green-600 text-2xl">✓</span>
          </div>
          <div>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              <Trans>All Set!</Trans>
            </h2>
            <p className="text-gray-600 text-base lg:text-lg">
              <Trans>
                No additional questions are needed for{" "}
                {selectedTypes.join(", ")}. You can proceed to the next step.
              </Trans>
            </p>
          </div>
          <div className="flex gap-4 justify-center">
            <Button
              variant="outline"
              onClick={onBack}
              className="px-6 h-12 lg:h-14 text-base border-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 font-bold rounded-xl transition-all duration-300"
            >
              <Trans>Back</Trans>
            </Button>
            <Button
              onClick={onNext}
              className="px-8 h-12 lg:h-14 text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
              variant="jema"
              size="lg"
            >
              <Trans>Continue to Photos</Trans>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If no procedure type(s) selected yet, show loading
  if (selectedTypes.length === 0 || procedureSections.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#166B93] mx-auto"></div>
          <p className="text-gray-600 text-base lg:text-lg">
            <Trans>Loading questions...</Trans>
          </p>
        </div>
      </div>
    );
  }

  const renderQuestion = (question: any) => {
    const value = formData[question.id];
    const error = errors[question.id];

    switch (question.type) {
      case "select":
        return (
          <div key={question.id} className="space-y-2 lg:space-y-3">
            <Label
              htmlFor={question.id}
              className="text-sm lg:text-base font-medium"
            >
              {question.label}
            </Label>
            <Select
              value={value}
              onValueChange={(val) => handleInputChange(question.id, val)}
            >
              <SelectTrigger
                className={`h-12 lg:h-14 text-base ${error ? "border-red-500" : ""}`}
              >
                <SelectValue
                  placeholder={i18n._(msg`Select an option`)}
                />
              </SelectTrigger>
              <SelectContent>
                {question.options.map((optionMsg: any) => {
                  const option = i18n._(optionMsg);
                  return (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {error && (
              <p className="text-red-500 text-xs lg:text-sm">{error}</p>
            )}
          </div>
        );

      case "multiselect":
        return (
          <div key={question.id} className="space-y-2 lg:space-y-3">
            <Label className="text-sm lg:text-base font-medium">
              {question.label}
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-3 p-3 lg:p-4 border rounded-md">
              {question.options.map((optionMsg: any) => {
                const option = i18n._(optionMsg);
                return (
                  <div
                    key={option}
                    className="flex items-center space-x-2 lg:space-x-3 min-h-touch"
                  >
                    <Checkbox
                      id={`${question.id}-${option}`}
                      checked={(value || []).includes(option)}
                      onCheckedChange={(checked) =>
                        handleMultiSelectChange(
                          question.id,
                          option,
                          checked as boolean,
                        )
                      }
                      className="w-4 h-4 lg:w-5 lg:h-5"
                    />
                    <Label
                      htmlFor={`${question.id}-${option}`}
                      className="text-sm lg:text-base font-normal cursor-pointer leading-tight"
                    >
                      {option}
                    </Label>
                  </div>
                );
              })}
            </div>
            {error && (
              <p className="text-red-500 text-xs lg:text-sm">{error}</p>
            )}
          </div>
        );

      case "textarea":
        return (
          <div key={question.id} className="space-y-2 lg:space-y-3">
            <Label
              htmlFor={question.id}
              className="text-sm lg:text-base font-medium"
            >
              {question.label}
            </Label>
            <Textarea
              id={question.id}
              value={value}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              className={`min-h-[80px] lg:min-h-[100px] text-base ${error ? "border-red-500" : ""}`}
              rows={3}
            />
            {error && (
              <p className="text-red-500 text-xs lg:text-sm">{error}</p>
            )}
          </div>
        );

      default:
        return (
          <div key={question.id} className="space-y-2 lg:space-y-3">
            <Label
              htmlFor={question.id}
              className="text-sm lg:text-base font-medium"
            >
              {question.label}
            </Label>
            <Input
              id={question.id}
              value={value}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              className={`h-12 lg:h-14 text-base ${error ? "border-red-500" : ""}`}
            />
            {error && (
              <p className="text-red-500 text-xs lg:text-sm">{error}</p>
            )}
          </div>
        );
    }
  };

  return (
    <div className="h-full">
      {/* Mobile: Traditional card layout */}
      <div className="lg:hidden">
        <ResponsiveContainer variant="standard">
          <Card className="w-full">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">
                <Trans>Additional dental questions</Trans>
              </CardTitle>
              <p className="text-gray-600 text-sm">
                <Trans>
                  Please answer the sections below for each procedure type you
                  selected.
                </Trans>
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-8">
                {procedureSections.map(({ typeKey, tree }) => (
                  <Fragment key={typeKey}>
                    <div className="border-b border-gray-100 pb-2">
                      <h3 className="text-base font-semibold text-gray-900">
                        {tree.title}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">
                        <Trans>Procedure type:</Trans> {typeKey}
                      </p>
                    </div>
                    <div className="space-y-6">
                      {tree.questions.map(renderQuestion)}
                    </div>
                  </Fragment>
                ))}
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <Button
                  onClick={handleSubmit}
                  className="w-full h-12 text-base hover:shadow-lg transition-all duration-200"
                  variant="jema"
                  size="lg"
                >
                  <Trans>Continue to Photos</Trans>
                </Button>
                <Button
                  variant="outline"
                  onClick={onBack}
                  className="w-full h-12 text-base border-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 font-bold rounded-xl transition-all duration-300"
                >
                  <Trans>Back</Trans>
                </Button>
              </div>
            </CardContent>
          </Card>
        </ResponsiveContainer>
      </div>

      {/* Desktop/Tablet: Modern full-screen layout */}
      <div className="hidden lg:block max-w-4xl mx-auto">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <h2 className="text-3xl xl:text-4xl font-bold text-gray-900">
              <Trans>Additional dental questions</Trans>
            </h2>
            <p className="text-lg text-gray-600">
              <Trans>
                Please answer the sections below for each procedure type you
                selected.
              </Trans>
            </p>
          </div>

          {/* Questions in modern card layout — one block per procedure type */}
          <div className="space-y-10">
            {procedureSections.map(({ typeKey, tree }) => (
              <div
                key={typeKey}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 xl:p-10"
              >
                <div className="mb-6 text-center">
                  <h3 className="text-xl font-bold text-gray-900">{tree.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    <Trans>Procedure type:</Trans> {typeKey}
                  </p>
                </div>
                <div className="space-y-8">{tree.questions.map(renderQuestion)}</div>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex gap-6 justify-center pt-8">
            <Button
              variant="outline"
              onClick={onBack}
              className="px-8 h-14 text-lg border-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 font-bold rounded-xl transition-all duration-300"
            >
              <Trans>Back</Trans>
            </Button>
            <Button
              onClick={handleSubmit}
              className="px-12 h-14 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
              variant="jema"
              size="lg"
            >
              <Trans>Continue to Photos</Trans>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
